import { useState, useEffect, Component, type ReactNode, type ErrorInfo } from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { useAuth } from "@/hooks/use-auth";
import { SiteHome, SiteServices, SiteWork, SiteReels, SiteJournal, SiteJournalPost, SiteAccess } from "@/pages/site/site-pages";
import AppShell from "@/components/app-shell";
import PoolsLayout from "@/components/pools-layout";
import Dashboard from "@/pages/dashboard";
import Strategy from "@/pages/strategy";
import ContentStudio from "@/pages/content-studio";
import AdCenter from "@/pages/ad-center";
import LeadDesk from "@/pages/lead-desk";
import LeadsPage from "@/pages/leads/desk/LeadsPage";
import LeadReportsPage from "@/pages/leads/desk/LeadReportsPage";
import HarryPage from "@/pages/harry";
import HarrySharedBrief from "@/pages/harry-shared-brief";
import ClawPage from "@/pages/claw";
import SettingsPage from "@/pages/settings";
import SaasAdminLogin from "@/pages/saas-admin-login";
import SaasAdminDashboard from "@/pages/saas-admin-dashboard";
import AdminDashboard from "@/pages/admin-dashboard";
import SignupPage from "@/pages/signup";
import PublicPage from "@/pages/public-page";
import PrivacyPolicy from "@/pages/privacy-policy";
import TermsOfService from "@/pages/terms-of-service";
import DataDeletion from "@/pages/data-deletion";
import PublicBooking from "./pages/public-booking";
import ManageAppointment from "./pages/manage-appointment";
// The classic form wizard (pages/onboarding.tsx) stays in the repo but is no longer routed: the chat is the one onboarding path.
import OnboardingChat from "@/pages/onboarding-chat";
import InviteAccept from "@/pages/invite-accept";
import EmailMarketing from "@/pages/email-marketing";
import ApiDocs from "@/pages/api-docs";
import DeepPlan from "@/pages/deep-plan";
import BlogPost from "@/pages/blog-post";
import SitePage from "@/pages/site-page";
import Status from "@/pages/status";
import JoinPage from "@/pages/join";
import ForgotPasswordPage from "@/pages/forgot-password";
import ResetPasswordPage from "@/pages/reset-password";
import PeggyAuditReport from "@/pages/peggy-audit-report";
import QaDashboard from "@/pages/qa-dashboard";
import Guide from "@/pages/guide";
import WidgetUI from "@/pages/widget-ui";
import ChatPreview from "@/pages/chat-preview";
import DraperHome from "@/pages/draper-home";
import PoolsIndex from "@/pages/pools-index";
import PoolDetailPage from "@/pages/pool-detail";
import PoolJoinPage from "@/pages/pool-join";
import ChaosPoolsLanding from "@/pages/chaos-pools-landing";
/** Chaos Pools is archived (Bader, 2026-11-02): its pages stay in the code but are only routed in a VITE_POOLS_ENABLED=true build. */
const POOLS_ENABLED = import.meta.env.VITE_POOLS_ENABLED === "true";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePublicConfig } from "@/hooks/use-pilot-mode";
import type { Workspace } from "@shared/schema";
import { shouldShowOnboarding } from "@shared/onboarding";
import { ThemeProvider } from "@/components/theme-provider";

function AccessDeniedScreen({ reason, status, pilotMode }: { reason: string; status?: string; pilotMode?: boolean }) {
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  async function startCheckout() {
    setCheckoutLoading(true);
    try {
      const r = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}), // one plan: the server picks the single active package
      });
      const data = await r.json();
      if (data.url) window.location.href = data.url;
    } catch {
      setCheckoutLoading(false);
    }
  }

  if (reason === "billing_required" && pilotMode) {
    // pilot: no plans or checkout; the team turns access on for pilot businesses
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="pilot-access-pending-screen">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-display font-bold text-foreground mb-3">Your account is approved</h2>
          <p className="text-sm text-muted-foreground mb-8 leading-relaxed">The DrPR team still has to switch on access for your pilot workspace. Please check back soon, or contact the team if it takes longer than expected.</p>
          <a href="/api/auth/logout" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground/5 border border-foreground/10 text-muted-foreground hover:text-foreground hover:bg-foreground/10 font-medium rounded-xl text-sm transition-all">
            Sign Out
          </a>
        </div>
      </div>
    );
  }

  if (reason === "billing_required") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white font-mono flex items-center justify-center px-6" data-testid="billing-required-screen">
        <div className="max-w-lg w-full">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/25 mb-6">Access Control_</p>
          <h2 className="text-3xl font-black uppercase tracking-tight mb-3 text-cyan-400">One Step Away</h2>
          <p className="text-sm text-white/50 mb-10 leading-relaxed font-sans">
            Your account has been approved. Complete your subscription to deploy your marketing agents and get started.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={startCheckout}
              disabled={checkoutLoading}
              className="flex items-center justify-center gap-2 px-8 py-4 bg-cyan-400 text-black font-black uppercase tracking-widest text-sm hover:bg-cyan-300 transition-colors disabled:opacity-60"
              data-testid="button-start-checkout"
            >
              {checkoutLoading ? <Loader2 size={16} className="animate-spin" /> : "Choose Your Plan"}
            </button>
            <a href="/api/auth/logout" className="text-center text-[11px] text-white/20 hover:text-white/40 transition-colors uppercase tracking-widest py-2">
              Sign Out
            </a>
          </div>
        </div>
      </div>
    );
  }

  const messages: Record<string, { title: string; desc: string }> = {
    waitlist: {
      title: status === "pending" ? "You're on the waitlist!" : status === "rejected" ? "Access not approved" : "Awaiting approval",
      desc: status === "pending"
        ? "Thanks for signing up! We're reviewing your application and will notify you when your spot is ready."
        : status === "rejected"
        ? "Unfortunately, your application wasn't approved at this time. Please contact us for more information."
        : "Your application is being processed. Hang tight!",
    },
    not_on_waitlist: {
      title: "Access Required",
      desc: "Drpr.ai is currently invite-only. Please join the waitlist on our landing page to request access.",
    },
    "Account suspended": {
      title: "Account Suspended",
      desc: "Your account has been suspended. Please contact support for assistance.",
    },
  };
  const msg = messages[reason] || messages["not_on_waitlist"];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="access-denied-screen">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-6">
          <Loader2 size={32} className={`text-blue-400 ${status === "pending" ? "animate-spin" : ""}`} />
        </div>
        <h2 className="text-2xl font-display font-bold text-foreground mb-3">{msg.title}</h2>
        <p className="text-sm text-muted-foreground mb-8 leading-relaxed">{msg.desc}</p>
        <div className="flex flex-col gap-3">
          {reason === "not_on_waitlist" && (
            <a href="/join" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-sm transition-all" data-testid="button-go-to-waitlist">
              Request Access
            </a>
          )}
          <a href="/api/auth/logout" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground/5 border border-foreground/10 text-muted-foreground hover:text-foreground hover:bg-foreground/10 font-medium rounded-xl text-sm transition-all" data-testid="button-logout-access-denied">
            Sign Out
          </a>
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { toast } = useToast();
  const [loc, setLocation] = useLocation();
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [checkoutRedirecting, setCheckoutRedirecting] = useState(false);
  // PILOT_MODE: no pricing step, no plan checkout, no "choose your plan" screen
  const { pilotMode, loaded: configLoaded } = usePublicConfig();

  const { data: accessCheck, isLoading: accessLoading } = useQuery<{ allowed: boolean; reason?: string; status?: string }>({
    queryKey: ["/api/access-check"],
    enabled: isAuthenticated,
  });

  const { data: workspaces, isLoading: wsLoading } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
    enabled: isAuthenticated && accessCheck?.allowed === true,
  });

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    const pendingInvite = POOLS_ENABLED ? sessionStorage.getItem("drpr_pool_invite") : null;
    if (pendingInvite && !loc.startsWith("/pools")) {
      setLocation(`/pools/join/${pendingInvite}`);
    }
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (!isAuthenticated || checkoutRedirecting || !accessCheck?.allowed || !configLoaded) return;
    const pendingPlan = sessionStorage.getItem("drpr_signup_plan");
    if (pendingPlan && pilotMode) {
      sessionStorage.removeItem("drpr_signup_plan"); // pilot: a plan picked on the old /signup page never starts a checkout
      return;
    }
    if (pendingPlan) {
      sessionStorage.removeItem("drpr_signup_plan");
      setCheckoutRedirecting(true);
      fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}), // one plan: the server picks the single active package
      })
        .then(r => {
          if (!r.ok) throw new Error("Checkout request failed");
          return r.json();
        })
        .then(data => {
          if (data.url) {
            window.location.href = data.url;
          } else {
            setCheckoutRedirecting(false);
            toast({ title: "Checkout unavailable", description: "Could not start checkout. You can subscribe later from Settings.", variant: "destructive" });
          }
        })
        .catch(() => {
          setCheckoutRedirecting(false);
          toast({ title: "Checkout error", description: "Something went wrong starting checkout. You can subscribe later from Settings.", variant: "destructive" });
        });
    }
  }, [isAuthenticated, checkoutRedirecting, accessCheck?.allowed, configLoaded, pilotMode]);

  if (isLoading || (isAuthenticated && accessLoading) || (isAuthenticated && accessCheck?.allowed && wsLoading) || checkoutRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <SiteHome />;
  }

  const isPoolRoute = loc.startsWith("/pools");
  const pendingPoolInvite = sessionStorage.getItem("drpr_pool_invite");

  // One onboarding path: the chat. Shown for no workspace, or while the user's only workspace has not finished onboarding,
  // so a reload mid-chat resumes the chat instead of dropping into a half-made workspace (shared/onboarding.ts).
  if (!onboardingComplete && shouldShowOnboarding(workspaces, (user as any)?.id) && !isPoolRoute && !pendingPoolInvite) {
    return (
      <OnboardingChat
        onComplete={() => {
          setOnboardingComplete(true);
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
        }}
      />
    );
  }

  if (accessCheck && !accessCheck.allowed && !isPoolRoute && !pendingPoolInvite) {
    return <AccessDeniedScreen reason={accessCheck.reason || "not_on_waitlist"} status={accessCheck.status} pilotMode={pilotMode} />;
  }

  return (
    <AppShell>
      <Component />
      
    </AppShell>
  );
}

function AdminOnlyRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  if (isLoading) return <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!isAuthenticated || !user?.isAdmin) return <div className="flex items-center justify-center min-h-screen text-muted-foreground text-sm">Access denied. Admin only.</div>;
  return (
    <AppShell>
      <Component />
    </AppShell>
  );
}

function PoolsAuthWall() {
  const [authMode, setAuthMode] = useState<"signup" | "signin">("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const { toast } = useToast();

  const PC = {
    bg:"#080e0a", card:"#0f1810", border:"#243225",
    gold:"#d4a017", cream:"#f0ebe0", muted:"#6a7a5a",
  };
  const inputStyle: React.CSSProperties = {
    width:"100%", padding:"12px 14px", borderRadius:10,
    background:"rgba(255,255,255,0.04)", border:`1px solid ${PC.border}`,
    color:PC.cream, fontSize:14, fontFamily:"inherit", outline:"none",
    boxSizing:"border-box",
  };

  async function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    setAuthError("");
    setAuthSubmitting(true);
    try {
      const endpoint = authMode === "signup" ? "/api/auth/register" : "/api/auth/login";
      // the server skips the waitlist only for a real pool invite code (a bare poolContext flag is ignored)
      const poolInviteCode = sessionStorage.getItem("drpr_pool_invite") || undefined;
      const body = authMode === "signup"
        ? { email, password, name, poolContext: true, poolInviteCode }
        : { email, password, poolContext: true, poolInviteCode };
      const r = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        setAuthError(data.error || "Something went wrong. Please try again.");
        return;
      }
      if (data.waitlisted) {
        setAuthError("Joining a pool needs the invite link from the pool's organiser. Open that link and sign up again.");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({ title: authMode === "signup" ? "Account created! Welcome to Chaos Pools." : "Welcome back!" });
    } catch {
      setAuthError("Network error. Please try again.");
    } finally {
      setAuthSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight:"100vh", background:PC.bg, color:PC.cream, fontFamily:"'Outfit',system-ui,sans-serif", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 20px", boxSizing:"border-box" }}>
      <style>{`*{box-sizing:border-box} input::placeholder{color:#6a7a5a} input:focus{border-color:#d4a017!important}`}</style>
      <div style={{ maxWidth:420, width:"100%" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(212,160,23,0.1)", border:"1px solid rgba(212,160,23,0.25)", borderRadius:20, padding:"4px 12px", marginBottom:16 }}>
            <span style={{ fontSize:10, fontWeight:700, color:PC.gold, textTransform:"uppercase", letterSpacing:"0.15em" }}>World Cup 2026 · Chaos Pools</span>
          </div>
          <div style={{ width:72, height:72, borderRadius:18, background:`linear-gradient(135deg,${PC.gold},#c8860d)`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px", fontSize:34, boxShadow:`0 0 28px rgba(212,160,23,0.3)` }}>⚽</div>
          <h1 style={{ fontSize:28, fontWeight:900, color:PC.cream, letterSpacing:"-0.03em", margin:"0 0 6px" }}>Chaos Pools</h1>
          <p style={{ fontSize:13, color:PC.muted, margin:0, lineHeight:1.5 }}>Pick groups · fill the bracket · win bragging rights.</p>
        </div>

        <div style={{ background:PC.card, border:`1px solid ${PC.border}`, borderTop:`2px solid ${PC.gold}`, borderRadius:14, padding:20, marginBottom:16 }}>
          <div style={{ display:"flex", background:"rgba(255,255,255,0.04)", borderRadius:8, padding:3, marginBottom:20, gap:3 }}>
            {(["signup","signin"] as const).map((m) => (
              <button key={m} data-testid={`button-pools-auth-mode-${m}`}
                onClick={() => { setAuthMode(m); setAuthError(""); }}
                style={{ flex:1, padding:"8px 0", borderRadius:6, border:"none", fontFamily:"inherit", fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.15s",
                  background: authMode === m ? PC.gold : "transparent",
                  color: authMode === m ? "#080e0a" : PC.muted,
                  textTransform:"uppercase", letterSpacing:"0.08em" }}>
                {m === "signup" ? "Create Account" : "Sign In"}
              </button>
            ))}
          </div>

          <form onSubmit={handleAuth} style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {authMode === "signup" && (
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:PC.muted, textTransform:"uppercase", letterSpacing:"0.1em", display:"block", marginBottom:6 }}>Your Name</label>
                <input data-testid="input-pools-auth-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" style={inputStyle} />
              </div>
            )}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:PC.muted, textTransform:"uppercase", letterSpacing:"0.1em", display:"block", marginBottom:6 }}>Email</label>
              <input data-testid="input-pools-auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} required />
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:PC.muted, textTransform:"uppercase", letterSpacing:"0.1em", display:"block", marginBottom:6 }}>Password</label>
              <div style={{ position:"relative" }}>
                <input data-testid="input-pools-auth-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={authMode === "signup" ? "Min. 8 characters" : "Your password"}
                  style={{ ...inputStyle, paddingRight:42 }} required minLength={authMode === "signup" ? 8 : 1} />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:PC.muted, padding:0, display:"flex" }}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {authError && (
              <p data-testid="text-pools-auth-error" style={{ fontSize:12, color:"#f87171", background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.2)", borderRadius:8, padding:"8px 12px", margin:0 }}>
                {authError}
              </p>
            )}

            <button data-testid="button-pools-auth-submit" type="submit" disabled={authSubmitting}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", padding:"14px 0", borderRadius:10,
                background: authSubmitting ? "rgba(212,160,23,0.5)" : `linear-gradient(135deg,${PC.gold},#c8860d)`,
                border:"none", color:"#080e0a", fontWeight:900, fontSize:14, cursor: authSubmitting ? "not-allowed" : "pointer",
                fontFamily:"inherit", boxShadow:`0 4px 20px rgba(212,160,23,0.25)`, marginTop:4 }}>
              {authSubmitting
                ? <Loader2 size={16} className="animate-spin" />
                : <><span>{authMode === "signup" ? "Create Free Account" : "Sign In"}</span><ArrowRight size={16} /></>}
            </button>
          </form>
        </div>

        <p style={{ textAlign:"center", fontSize:11, color:PC.muted, lineHeight:1.6 }}>
          Free to play. Drpr.ai does not collect or hold any funds.
        </p>
      </div>
    </div>
  );
}

function PoolRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight:"100vh", background:"#080e0a", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <Loader2 className="h-7 w-7 animate-spin" style={{ color:"#d4a017" }} />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <PoolsAuthWall />;
  }
  return (
    <PoolsLayout>
      <Component />
    </PoolsLayout>
  );
}

function SaasAdminOwnerRoute() {
  const [admin, setAdmin] = useState<{ id: number; username: string; displayName: string; role: string } | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch("/api/saas-admin/me", { credentials: "include" })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("Not authenticated");
      })
      .then(setAdmin)
      .catch(() => setAdmin(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#030308]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!admin) {
    return <SaasAdminLogin onLogin={setAdmin} />;
  }

  if (admin.role !== "owner") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background text-muted-foreground text-sm">
        Owner access required.
      </div>
    );
  }

  return (
    <AppShell>
      <QaDashboard />
    </AppShell>
  );
}

function SaasAdminRoute() {
  const [admin, setAdmin] = useState<{ id: number; username: string; displayName: string; role: string } | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch("/api/saas-admin/me", { credentials: "include" })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("Not authenticated");
      })
      .then(setAdmin)
      .catch(() => setAdmin(null))
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    await fetch("/api/saas-admin/logout", { method: "POST", credentials: "include" });
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#030308]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!admin) {
    return <SaasAdminLogin onLogin={setAdmin} />;
  }

  return <SaasAdminDashboard admin={admin} onLogout={handleLogout} />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={() => <PrivateRoute component={DraperHome} />} />
      <Route path="/draper" component={() => <PrivateRoute component={DraperHome} />} />
      <Route path="/draper-home" component={() => <PrivateRoute component={DraperHome} />} />
      <Route path="/dashboard" component={() => <Redirect to="/overview" />} />
      <Route path="/overview" component={() => <PrivateRoute component={Dashboard} />} />
      <Route path="/strategy" component={() => <PrivateRoute component={Strategy} />} />
      <Route path="/content" component={() => <PrivateRoute component={ContentStudio} />} />
      <Route path="/ads" component={() => <PrivateRoute component={AdCenter} />} />
      <Route path="/leads/classic" component={() => <PrivateRoute component={LeadDesk} />} />
      <Route path="/leads/reports" component={() => <PrivateRoute component={LeadReportsPage} />} />
      <Route path="/leads" component={() => <PrivateRoute component={LeadsPage} />} />
      <Route path="/email-marketing" component={() => <Redirect to="/leads?tab=campaigns" />} />
      <Route path="/opportunities" component={() => <Redirect to="/harry" />} />
      <Route path="/harry" component={() => <PrivateRoute component={HarryPage} />} />
      <Route path="/claw" component={() => <PrivateRoute component={ClawPage} />} />
      <Route path="/brief/:token" component={HarrySharedBrief} />
      <Route path="/team" component={() => <Redirect to="/settings" />} />
      <Route path="/invite/:token" component={InviteAccept} />
      <Route path="/p/:slug" component={PublicPage} />
      <Route path="/launch" component={() => <PrivateRoute component={DeepPlan} />} />
      <Route path="/guide" component={() => <PrivateRoute component={Guide} />} />
      <Route path="/settings" component={() => <PrivateRoute component={SettingsPage} />} />
      <Route path="/join" component={JoinPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/services" component={SiteServices} />
      <Route path="/work" component={SiteWork} />
      <Route path="/see-it-work" component={SiteReels} />
      <Route path="/journal" component={SiteJournal} />
      <Route path="/journal/:slug" component={SiteJournalPost} />
      <Route path="/access" component={SiteAccess} />
      <Route path="/about" component={() => <Redirect to="/services" />} />
      <Route path="/why-drpr" component={() => <Redirect to="/services" />} />
      <Route path="/signup" component={SignupPage} />
      {/* the cancellation email and agency settings link /pricing: one plan, shown on the sign-up page */}
      <Route path="/pricing" component={() => <Redirect to="/signup" />} />
      <Route path="/saas-admin" component={SaasAdminRoute} />
      <Route path="/admin" component={() => <PrivateRoute component={AdminDashboard} />} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/data-deletion/:code" component={DataDeletion} />
      <Route path="/book/:slug" component={PublicBooking} />
      <Route path="/appointment/:token" component={ManageAppointment} />
      <Route path="/blog" component={() => <Redirect to="/journal" />} />
      <Route path="/blog/:slug" component={BlogPost} />
      <Route path="/page/:slug" component={SitePage} />
      <Route path="/api-docs" component={ApiDocs} />
      <Route path="/status" component={Status} />
      {/* Chaos Pools is archived (2026-11-02): served only in a build with VITE_POOLS_ENABLED=true */}
      {POOLS_ENABLED && <Route path="/chaos-pools" component={ChaosPoolsLanding} />}
      {POOLS_ENABLED && <Route path="/pools/join/:code" component={PoolJoinPage} />}
      {POOLS_ENABLED && <Route path="/pools" component={() => <PoolRoute component={PoolsIndex} />} />}
      {POOLS_ENABLED && <Route path="/pools/:poolId" component={() => <PoolRoute component={PoolDetailPage} />} />}
      {!POOLS_ENABLED && <Route path="/chaos-pools" component={() => <Redirect to="/" />} />}
      {!POOLS_ENABLED && <Route path="/pools/:rest*" component={() => <Redirect to="/" />} />}
      <Route path="/dev/peggy-audit" component={() => <AdminOnlyRoute component={() => (
        <div className="p-6 overflow-auto"><PeggyAuditReport /></div>
      )} />} />
      <Route path="/qa" component={SaasAdminOwnerRoute} />
      <Route path="/widget-ui" component={WidgetUI} />
      <Route path="/chat-preview" component={() => <PrivateRoute component={ChatPreview} />} />
      <Route component={NotFound} />
    </Switch>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6" data-testid="error-boundary">
          <div className="max-w-lg w-full text-center space-y-4">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/25">Something went wrong_</p>
            <h2 className="text-2xl font-black uppercase tracking-tight text-red-400">Unexpected Error</h2>
            <p className="text-sm text-white/50 font-sans leading-relaxed">{this.state.error?.message || "An unexpected error occurred."}</p>
            <button
              data-testid="button-error-boundary-reload"
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              className="mt-4 px-6 py-3 bg-white/10 hover:bg-white/20 text-white text-sm font-bold uppercase tracking-widest transition-colors rounded-lg"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
